import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import React, { useState, useEffect, useMemo, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ControlpanelPrimaryButton, ControlpanelSecondaryButton, GrayIconButton } from '../../../components/StyledButton';
import { CustomDialog } from '../../../components/StyledComponents';
import CustomDateTimePicker from '../../../components/CustomDateTimeRangePicker';
import CustomInput from './../../../components/CustomInput';
import CustomSelect from './../../../components/CustomSelect';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { editEventlogRequest } from '../../../axios/Eventlog/EventLogApi';
import { useAuth } from '../../../context/AuthContext';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getShiftByUser } from '../../../axios/Shift/ShiftApi';
dayjs.extend(utc);
dayjs.extend(timezone);

const normalizeTime = (time) => {
   if (!time) return null;

   if (/^\d{2}:\d{2}$/.test(time)) {
      return `${time}:00`;
   }

   if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
      return time;
   }

   return time;
};

const toDateTimeValue = (date, time) => {
   if (!date) return null;
   if (!time) return date;

   return dayjs(
      `${dayjs(date).format('YYYY-MM-DD')} ${normalizeTime(time) || time}`
   ).toDate();
};

const EventDateTimeField = memo(({
   selectDate,
   selectTime,
   dateTimeFormat,
   onDateTimeChange,
}) => (
   <CustomDateTimePicker
      mode="singleTime"
      value={toDateTimeValue(selectDate, selectTime)}
      dateTimeFormat={dateTimeFormat}
      onChange={(newDateTime) => {
         const d = dayjs(newDateTime);
         if (!d.isValid()) return;
         onDateTimeChange(d.toDate(), d.format('HH:mm:ss'));
      }}
   />
), (prev, next) =>
   prev.selectDate === next.selectDate &&
   prev.selectTime === next.selectTime &&
   prev.dateTimeFormat === next.dateTimeFormat
);

const EditEventDialogByUser = ({ onClose, usersData, shiftsData, onRefresh, info }) => {
   const { genaralSettingTimeZone, profile, dateTimeFormat } = useAuth();
   const { t } = useTranslation();
   const [loadingCount, setLoadingCount] = useState(0);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const isDialogLoading = loadingCount > 0 || isSubmitting;
   const MAX_DESCRIPTION_LENGTH = 255;
   const [shiftsData1, setShiftsData] = useState([]);

   const handleCloseDialog = () => {
      onClose();
   };

   const handleInputChange = (e) => {
      const { name } = e.target;
      let { value } = e.target;

      if (name === 'description') {
         value = value.slice(0, MAX_DESCRIPTION_LENGTH);
      }

      setFormData((prevData) => ({
         ...prevData,
         [name]: value,
      }));
   };

   const browserTZ = dayjs.tz.guess();

   const initialFormData = {
      eventType: info?.eventType === 'CHECK_IN' ? 1 : info?.eventType === 'CHECK_OUT' ? 2 : '',
      username: info.fullName,
      selectDate: info.eventDate ? new Date(info.eventDate) : new Date(),
      selectDateOld: info.eventDate ? new Date(info.eventDate) : new Date(),
      selectTime: info?.timeGMT
         ? dayjs
            .utc(info.timeGMT)
            .tz(browserTZ)
            .format("HH:mm:ss")
         : "00:00:00",

      // ✅ DÙNG GENERAL SETTING
      timeZone: browserTZ,
      // shift: shift ? shift.shiftId : '',
      shift: info?.shiftId,
      // shiftDate: info?.shiftDate
      //    ? (() => {
      //       const [datePart, timePart] = info.shiftDate.split(' ');
      //       const [year, month, day] = datePart.split('-').map(Number);
      //       const [hour, minute, second] = timePart.split(':').map(Number);
      //       return new Date(year, month - 1, day, hour, minute, second);
      //    })()
      //    : new Date(),
      descriptionOld: info?.description || '',
      timeZoneOld: info?.timeZone ? info.timeZone : '',
      shiftOld: info?.shiftName ? info?.shiftName : '',
      selectTimeOld: info?.eventTime ? info?.eventTime : '',
      description: '',
      location: info?.location || '',
      userId: profile.userId,
      requestTypeId: 2,
      eventAttendanceId: info.eventAttendanceId,
      // shiftIdOld: info.shiftId
   };

   const buildAttendanceDateTimeUTC = (date, time) => {
      if (!date || !time) return null;

      const datePart = format(date, 'yyyy-MM-dd');

      // 👉 tạo datetime theo timezone công ty
      const companyDateTime = dayjs.tz(
         `${datePart} ${time}`,
         'YYYY-MM-DD HH:mm:ss',
         browserTZ
      );

      // 👉 convert sang UTC gửi backend
      return companyDateTime.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
   };

   const handleSubmitEditEventLogRequest = async () => {
      const attendanceDateTime = buildAttendanceDateTimeUTC(
         formData.selectDate,
         formData.selectTime,
      );

      const buildAttendanceDateTimeUTCFromInfo = (info, timeZone) => {
         if (!info?.eventDate || !info?.eventTime || !timeZone) return null;

         const sign = timeZone.startsWith("-") ? -1 : 1;
         const [hh, mm] = timeZone.slice(1).split(":").map(Number);
         const offsetMinutes = sign * (hh * 60 + mm);

         return dayjs(
            `${info.eventDate} ${info.eventTime}`,
            "YYYY-MM-DD HH:mm:ss"
         )
            .subtract(offsetMinutes, "minute")
            .format("YYYY-MM-DDTHH:mm:ss[Z]");
      };

      const oldAttendanceDateTime = info.timeGMT
         ? dayjs.utc(info.timeGMT).format("YYYY-MM-DDTHH:mm:ss[Z]")
         : null;

      const oldDataConvert = {
         id: info.eventAttendanceId,
         userId: info.userId,
         shiftId: info.shiftId,
         eventTypeId: info.eventType === 'CHECK_IN' ? 1 : 2,
         attendanceDateTime: oldAttendanceDateTime,
         timeZone: info.timeZone || genaralSettingTimeZone,
      };


      const payload = {
         ...formData,
         attendanceDateTime,
         selectDate: format(formData.selectDate, 'yyyy-MM-dd'),
         selectDateOld: format(formData.selectDateOld, 'yyyy-MM-dd'),
         oldData: JSON.stringify(oldDataConvert),
         langId: localStorage.getItem("i18nextLngId")
      };

      setIsSubmitting(true);
      try {
         const response = await editEventlogRequest(payload);
         if (response?.data?.responseCode === 0) {
            handleCloseDialog();
            toast.success(t('common.add-request-successfully'));
            onRefresh();
         } else {
            toast.error(response?.data?.message);
         }
      } catch (error) {
         if (error.response) {
            if (error.response.status !== 401) {
               toast.error(t(error?.response.data));
            }
         } else {
            toast.error(t('common.network-timeout'));
         }
      } finally {
         setIsSubmitting(false);
      }
   };

   const [formData, setFormData] = useState(initialFormData);

   const handleDateTimeChange = useCallback((newDate, newTime) => {
      setFormData((prev) => ({
         ...prev,
         selectDate: newDate,
         selectTime: newTime,
      }));
   }, []);

   const sortedShifts = [...shiftsData1].sort((a, b) => b.shiftId - a.shiftId);

   const eventDateTimeKey = useMemo(() => {
      if (!formData.selectDate || !formData.selectTime) return '';
      return `${dayjs(formData.selectDate).format('YYYY-MM-DD')}_${formData.selectTime}`;
   }, [formData.selectDate, formData.selectTime]);

   useEffect(() => {
      if (!formData.userId || !eventDateTimeKey) return;

      const fetchShift = async () => {
         try {
            setLoadingCount((prev) => prev + 1);

            const dateTimeUTC = buildAttendanceDateTimeUTC(
               formData.selectDate,
               formData.selectTime
            );

            const payload = {
               userId: formData.userId,        // ✅ user đang chọn
               dateTime: dateTimeUTC,          // ✅ ISO8601 UTC (+0)
               langId: localStorage.getItem("i18nextLngId"),
            };

            const response = await getShiftByUser(payload);
            setShiftsData(response.data.data || []);
         } catch (error) {
            toast.error(t(error?.response?.data));
         } finally {
            setLoadingCount((prev) => Math.max(prev - 1, 0));
         }
      };

      fetchShift();
   }, [formData.userId, eventDateTimeKey]);

   return (
      <CustomDialog open transitionDuration={300} maxWidth={'xl'} loading={isDialogLoading} onClose={handleCloseDialog} keepMounted disableEnforceFocus>
         <DialogTitle>
            <div className='flex justify-between'>
               <div className='flex items-center gap-4'>
                  <p className='text-xl font-semibold '>{t('common.request-edit-event')}</p>
               </div>
               <GrayIconButton onClick={handleCloseDialog} disabled={isDialogLoading}>
                  <CloseIcon />
               </GrayIconButton>
            </div>
         </DialogTitle>
         <DialogContent>
            <div className='pt-3 space-y-3'>
               <div className='space-y-3'>
                  <p>{t('common.event-type')}</p>
                  <FormControl
                     component='fieldset'
                     sx={{
                        '.MuiFormGroup-root.MuiFormGroup-row': {
                           gap: '20px',
                        },
                     }}
                  >
                     <RadioGroup row aria-label='Event type' name='eventType' value={formData.eventType} onChange={handleInputChange}>
                        <FormControlLabel value={1} control={<Radio size='small' />} label={t('attendance.CHECK_IN')} />
                        <FormControlLabel value={2} control={<Radio size='small' />} label={t('attendance.CHECK_OUT')} />
                     </RadioGroup>
                  </FormControl>
               </div>

               <div className='grid grid-cols-12 md:gap-x-3 gap-y-3'>
                  <div className='col-span-12 md:col-span-6 space-y-3'>
                     <label>{t('common.userName')}</label>
                     <CustomInput
                        name='username'
                        inputType='text'
                        readOnly={true}
                        value={formData.username}
                        handleInputChange={handleInputChange}
                        labelEnable={false}
                        placeholder={t('common.userName')}
                     />
                  </div>

                  <div className='col-span-12 md:col-span-6 space-y-3'>
                     <label>{t('common.date-time')}<span className='text-[#EF4444] ml-1'>*</span></label>
                     <EventDateTimeField
                        selectDate={formData.selectDate}
                        selectTime={formData.selectTime}
                        dateTimeFormat={dateTimeFormat}
                        onDateTimeChange={handleDateTimeChange}
                     />
                  </div>

                  <div className='col-span-12 md:col-span-12 space-y-3'>
                     <label>{t('common.shift')}<span className='text-[#EF4444] ml-1'>*</span></label>
                     <CustomSelect
                        name='shift'
                        value={formData.shift}
                        onChange={handleInputChange}
                        options={sortedShifts}
                        dataType='shift'
                     />
                  </div>

                  {/* <div className='col-span-12 md:col-span-6 space-y-3'>
                     <label className='text-[#171717]'>{t('common.location')}</label>
                     <CustomInput
                        inputType='text'
                        readOnly={true}
                        value={formData.location}
                        labelEnable={false}
                     />
                  </div> */}

                  <div className='col-span-12 space-y-3'>
                     <label>{t('common.reason')}<span className='text-[#EF4444] ml-1'>*</span></label>
                     <CustomInput
                        name='description'
                        inputType='text'
                        value={formData.description}
                        handleInputChange={handleInputChange}
                        labelEnable={false}
                        placeholder={t('common.reason')}
                     />
                     <div
                        className={`flex justify-end text-xs ${formData.description?.length >= MAX_DESCRIPTION_LENGTH
                           ? 'text-red-500'
                           : 'text-gray-500'
                           }`}
                     >
                        {(formData.description?.length || 0)}/{MAX_DESCRIPTION_LENGTH}
                     </div>
                  </div>
               </div>
            </div>
         </DialogContent>
         <DialogActions>
            <div className='flex gap-2 px-4 py-4'>
               <ControlpanelSecondaryButton onClick={handleCloseDialog} disabled={isDialogLoading}>
                  <p>{t('button.close')}</p>
               </ControlpanelSecondaryButton>
               <ControlpanelPrimaryButton
                  onClick={() => {
                     handleSubmitEditEventLogRequest();
                  }}
                  disabled={isDialogLoading || !formData.description?.trim()}
                  sx={{
                     borderRadius: '10px',
                     textDecoration: 'none'
                  }}
               >
                  <p>{t('button.update-request')}</p>
               </ControlpanelPrimaryButton>
            </div>
         </DialogActions>
      </CustomDialog>
   );
};

export default EditEventDialogByUser;
